import { useEffect } from 'react';
import { Button, Divider, Group, Modal, PasswordInput, Select, Stack, Switch, Text, TextInput } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { SpecialtyInput } from './SpecialtyInput';
import dayjs from 'dayjs';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { canManageRole } from '../lib/permissions';
import { GenderInput } from './QuickCreate';
import { MultiPhoneEditor, MultiAddressEditor, type PhoneItem, type AddressItem } from './MultiContact';
import { uzDateProps } from '../lib/dateInput';

/** PATCH /staff/:id qabul qiladigan maydonlar uchun minimal foydalanuvchi ko'rinishi. */
export interface StaffEditableUser {
  id: string;
  login: string;
  fullName: string;
  role: string;
  phone?: string | null;
  gender?: string | null;
  birthDate?: string | null;
  isActive?: boolean;
  canEnterTestScores?: boolean;
  specialty?: string | null;
  phones?: { id: string; label: string; number: string; isPrimary: boolean }[];
  addresses?: { id: string; label: string; address: string; isPrimary: boolean }[];
}

// PARENT/STUDENT bu yerda yo'q — ular o'quvchi kartochkasidan yaratiladi (backend ham 400 qaytaradi).
const ROLE_VALUES = ['DIRECTOR', 'ADMIN', 'ACCOUNTANT', 'TEACHER', 'HOMEROOM_TEACHER', 'RECEPTION'];
// Rol / "test oluvchi ustoz" bayrog'ini faqat boshqaruv rollari o'zgartiradi (backend ham shu ro'yxatni tekshiradi).
const MANAGER_ROLES = ['SUPER_ADMIN', 'DIRECTOR', 'ADMIN', 'ACCOUNTANT', 'RECEPTION'];
const TEACHER_ROLES = ['TEACHER', 'HOMEROOM_TEACHER'];
// MultiContact dagi birinchi tayyor belgilar bilan bir xil (yorliq DB'ga o'zbekcha yoziladi, tarjima qilinmaydi).
const DEFAULT_PHONE_LABEL = 'Doimiy';
const DEFAULT_ADDRESS_LABEL = 'Yashash';

const apiErr = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? fallback;

/** ISO sanani "YYYY-MM-DD" ga keltiradi (backend kontrakti aynan shu shaklni kutadi). */
const dayKey = (v: string | Date | null | undefined) => (v ? dayjs(v).format('YYYY-MM-DD') : null);

const samePayload = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** Telefonlar jadvali bo'sh bo'lsa — eski yagona `phone` ustuni birinchi qator sifatida ko'chiriladi. */
function seedPhones(u: StaffEditableUser): PhoneItem[] {
  const list = (u.phones ?? []).map((p) => ({ label: p.label, number: p.number, isPrimary: p.isPrimary }));
  if (list.length) return list;
  return u.phone ? [{ label: DEFAULT_PHONE_LABEL, number: u.phone, isPrimary: true }] : [];
}

const seedAddresses = (u: StaffEditableUser): AddressItem[] =>
  (u.addresses ?? []).map((a) => ({ label: a.label, address: a.address, isPrimary: a.isPrimary }));

/** Bo'sh qatorlarni tashlaydi va ro'yxat bo'sh bo'lmasa aniq bitta `isPrimary` qoldiradi. */
function cleanPhones(items: PhoneItem[]): PhoneItem[] {
  const list = items
    .map((p) => ({ label: p.label.trim() || DEFAULT_PHONE_LABEL, number: p.number.trim(), isPrimary: p.isPrimary }))
    .filter((p) => p.number);
  if (list.length && !list.some((p) => p.isPrimary)) list[0].isPrimary = true;
  return list;
}

function cleanAddresses(items: AddressItem[]): AddressItem[] {
  const list = items
    .map((a) => ({ label: a.label.trim() || DEFAULT_ADDRESS_LABEL, address: a.address.trim(), isPrimary: a.isPrimary }))
    .filter((a) => a.address);
  if (list.length && !list.some((a) => a.isPrimary)) list[0].isPrimary = true;
  return list;
}

/**
 * XODIM / O'QITUVCHI TAHRIRLASH — YAGONA to'liq forma (T2).
 *
 * Xodim va o'qituvchi kartochkalari uchun bitta modal: profil (F.I.Sh, jins, tug'ilgan sana),
 * kirish ma'lumotlari (login, parolni qayta tayinlash), rol, "test oluvchi ustoz" bayrog'i,
 * qo'shimcha telefonlar va manzillar. Hammasi BITTA `PATCH /staff/:id` so'rovi bilan ketadi.
 *
 * MUHIM 1: so'rovga faqat HAQIQATAN o'zgargan maydonlar qo'shiladi. Aks holda o'z kartochkasini
 * ochgan admin `role` ni o'zgartirmagan holda ham 403 ("O'z rolingizni o'zgartira olmaysiz")
 * olardi, o'z loginini yuborish esa keraksiz bandlik tekshiruviga tushardi.
 *
 * MUHIM 2: telefon/manzil bu yerda O'RNIGA QO'YISH (replace) semantikasi bilan yuboriladi
 * (`PATCH /staff/:id { phones, addresses }`). Shu sabab AYNI kartochkada bittalab ishlaydigan
 * `ContactsManager` faqat KO'RISH rejimida (`readOnly`) ko'rsatilishi kerak — ikkala usul
 * aralashsa qatorlar id lari kutilmaganda almashadi (backend kontraktidagi ogohlantirish).
 *
 * Bloklash/blokdan chiqarish ATAYLAB bu yerda emas: u sabab so'raydigan o'z modaliga ega
 * (`BlockControl`) va kartochkaning "Holat" bo'limida turadi — modal ichida modal ochilmasin.
 */
export function StaffEditModal({
  opened, onClose, staff, onSaved, title,
}: {
  opened: boolean;
  onClose: () => void;
  staff: StaffEditableUser;
  /** Muvaffaqiyatli saqlangach — query invalidatsiyasi (kartochka + ro'yxatlar). */
  onSaved: () => void;
  title?: string;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { user } = useAuth();
  const isSelf = user?.id === staff.id;
  const canManage = MANAGER_ROLES.includes(user?.role ?? '');
  // O'z rolini o'zgartirish backendda ham taqiqlangan — tanlagichni oldindan qulflaymiz.
  const canEditRole = canManage && !isSelf;

  const form = useForm({
    initialValues: {
      fullName: '',
      specialty: '',
      gender: '',
      birthDate: null as Date | null,
      login: '',
      password: '',
      role: 'TEACHER',
      canEnterTestScores: false,
      phones: [] as PhoneItem[],
      addresses: [] as AddressItem[],
    },
    validate: {
      fullName: (v) => (!v.trim() ? t('staffPage.fullNameRequired') : null),
      login: (v) => (v.trim().length < 3 ? t('staffPage.loginMin') : null),
      // Parol IXTIYORIY: bo'sh qoldirilsa eski parol o'zgarmaydi.
      password: (v) => (v && v.length < 5 ? t('staffPage.passwordMin') : null),
    },
  });

  // Formani modal HAR ochilganda serverdagi qiymatlar bilan to'ldiramiz
  // (yopib-ochgan admin oldingi kiritmalarini emas, joriy holatni ko'rsin).
  useEffect(() => {
    if (!opened) return;
    form.setValues({
      fullName: staff.fullName,
      specialty: staff.specialty ?? '',
      gender: staff.gender ?? '',
      birthDate: staff.birthDate ? new Date(staff.birthDate) : null,
      login: staff.login,
      password: '',
      role: staff.role,
      canEnterTestScores: !!staff.canEnterTestScores,
      phones: seedPhones(staff),
      addresses: seedAddresses(staff),
    });
    form.clearErrors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, staff.id]);

  const mut = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.patch(`/staff/${staff.id}`, body),
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('staffPage.saved') });
      qc.invalidateQueries({ queryKey: ['staff-specialties'] }); // yangi qiymat takliflarga qo'shilsin
      onSaved();
      onClose();
    },
    onError: (e) => notifications.show({ color: 'red', message: apiErr(e, t('common.error')) }),
  });

  const submit = (v: typeof form.values) => {
    const body: Record<string, unknown> = {};

    const fullName = v.fullName.trim();
    if (fullName !== staff.fullName) body.fullName = fullName;
    /* Bo'sh satr ham YUBORILADI — admin mutaxassislikni ataylab tozalagan
       bo'lishi mumkin; `undefined` qoldirsak server uni "tegilmadi" deb
       tushunardi va eski qiymat qolib ketardi. */
    const specialty = v.specialty.trim();
    if (specialty !== (staff.specialty ?? '')) body.specialty = specialty;
    if (v.gender && v.gender !== (staff.gender ?? '')) body.gender = v.gender;
    const birthDate = dayKey(v.birthDate);
    if (birthDate !== dayKey(staff.birthDate)) body.birthDate = birthDate;

    const login = v.login.trim();
    if (login !== staff.login) body.login = login;
    if (v.password) body.password = v.password;
    if (canEditRole && v.role !== staff.role) body.role = v.role;

    // Bayroq FAQAT o'qituvchi rolida yuboriladi — aks holda backend 400 qaytaradi.
    const finalRole = (body.role as string | undefined) ?? staff.role;
    if (canManage && TEACHER_ROLES.includes(finalRole) && v.canEnterTestScores !== !!staff.canEnterTestScores) {
      body.canEnterTestScores = v.canEnterTestScores;
    }

    const phones = cleanPhones(v.phones);
    if (!samePayload(phones, cleanPhones(seedPhones(staff)))) body.phones = phones;
    const addresses = cleanAddresses(v.addresses);
    if (!samePayload(addresses, cleanAddresses(seedAddresses(staff)))) body.addresses = addresses;

    if (!Object.keys(body).length) {
      notifications.show({ color: 'blue', message: t('staffEdit.noChanges') });
      onClose();
      return;
    }
    mut.mutate(body);
  };

  // Ro'yxatda bo'lmagan rol (masalan SUPER_ADMIN) ham ko'rinsin — aks holda tanlagich bo'sh turardi.
  // O'zidan yuqori rolni tayinlab bo'lmaydi (buxgalter direktor/admin bera olmaydi) — server ham rad etadi.
  const tayinlanadi = ROLE_VALUES.filter((r) => canManageRole(user?.role, r));
  const roleOptions = (tayinlanadi.includes(staff.role) ? tayinlanadi : [staff.role, ...tayinlanadi])
    .map((r) => ({ value: r, label: t(`roles.${r}`) }));

  return (
    <Modal opened={opened} onClose={onClose} title={title ?? t('staffEdit.title')} centered size="lg">
      <form onSubmit={form.onSubmit(submit)}>
        <Stack>
          <TextInput label={t('staffPage.fullName')} required {...form.getInputProps('fullName')} />
          <SpecialtyInput
            value={form.values.specialty}
            onChange={(v) => form.setFieldValue('specialty', v)}
          />
          <GenderInput {...form.getInputProps('gender')} />
          <DateInput {...uzDateProps}
            label={t('staffPage.birthDate')}
            {...form.getInputProps('birthDate')}
          />

          <Divider label={t('staffEdit.account')} labelPosition="left" />
          <TextInput label={t('staffPage.login')} required {...form.getInputProps('login')} />
          <PasswordInput
            label={t('staffEdit.newPassword')}
            description={t('staffEdit.passwordHint')}
            placeholder="••••••"
            autoComplete="new-password"
            {...form.getInputProps('password')}
          />
          <Select
            label={t('staffPage.role')}
            data={roleOptions}
            allowDeselect={false}
            disabled={!canEditRole}
            description={canEditRole ? undefined : t('staffEdit.roleLocked')}
            comboboxProps={{ withinPortal: true }}
            {...form.getInputProps('role')}
          />
          {canManage && TEACHER_ROLES.includes(form.values.role) && (
            <Switch
              label={t('examsPage.testTeacher')}
              description={t('examsPage.testTeacherHint')}
              {...form.getInputProps('canEnterTestScores', { type: 'checkbox' })}
            />
          )}
          <Text size="xs" c="dimmed">{t('staffEdit.blockHint')}</Text>

          <Divider label={t('teacherDetail.contacts')} labelPosition="left" />
          <MultiPhoneEditor value={form.values.phones} onChange={(v) => form.setFieldValue('phones', v)} />
          <MultiAddressEditor value={form.values.addresses} onChange={(v) => form.setFieldValue('addresses', v)} />

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onClose}>{t('common.cancel')}</Button>
            <Button type="submit" loading={mut.isPending}>{t('common.save')}</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
